// Safari ignores user-scalable=no. Cancel page-zoom gestures explicitly while
// leaving single-finger scrolling, text selection and native media controls alone.
export function installZoomGuard(doc: Document = document): () => void {
  const removers: (() => void)[] = [];
  const listen = (name: string, listener: EventListener): void => {
    doc.addEventListener(name, listener, { passive: false, capture: true });
    removers.push(() => doc.removeEventListener(name, listener, true));
  };
  const cancel = (event: Event): void => {
    if (event.cancelable) event.preventDefault();
  };
  for (const name of ["gesturestart", "gesturechange", "gestureend"])
    listen(name, cancel);
  for (const name of ["touchstart", "touchmove"])
    listen(name, (event) => {
      if ((event as TouchEvent).touches.length > 1) cancel(event);
    });
  listen("wheel", (event) => {
    if ((event as WheelEvent).ctrlKey) cancel(event);
  });
  listen("keydown", (event) => {
    const key = event as KeyboardEvent;
    if ((key.ctrlKey || key.metaKey) && ["+", "=", "-", "0"].includes(key.key))
      cancel(event);
  });
  let previousTap:
    | { time: number; x: number; y: number; target: EventTarget | null }
    | undefined;
  listen("touchend", (event) => {
    const touchEvent = event as TouchEvent;
    const touch = touchEvent.changedTouches[0];
    if (
      !touch ||
      touchEvent.touches.length ||
      touchEvent.changedTouches.length !== 1
    ) {
      previousTap = undefined;
      return;
    }
    if (
      (event.target as Element | null)?.closest?.(
        "input,textarea,select,[contenteditable],video,audio",
      )
    ) {
      previousTap = undefined;
      return;
    }
    if (
      previousTap &&
      event.timeStamp - previousTap.time < 300 &&
      previousTap.target === event.target &&
      Math.hypot(touch.clientX - previousTap.x, touch.clientY - previousTap.y) <
        24
    )
      cancel(event);
    previousTap = {
      time: event.timeStamp,
      x: touch.clientX,
      y: touch.clientY,
      target: event.target,
    };
  });
  return () => removers.forEach((remove) => remove());
}
